/**
 * ServiceAccountsView -- admin management of B2B API keys (P119.4).
 *
 * Route: /admin/service-accounts. Admin-only. Mint / list / rotate /
 * enable-disable / revoke the service-account keys a B2B consumer uses
 * against the external surface (/v1/ext). The plaintext key is shown
 * ONCE (on create + rotate) in a copy banner that warns it won't be
 * shown again. Hardcoded English to match the sibling shared/admin views.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import {
  useCreateServiceAccount,
  useDeleteServiceAccount,
  useRotateServiceAccount,
  useServiceAccounts,
  useUpdateServiceAccount,
} from "shared/api/serviceAccounts";
import { SERVICE_SCOPES, type ServiceAccount } from "shared/api/types";
import { OutboundContract } from "shared/admin/OutboundContract";
import { formatDateTime } from "shared/time/formatDate";
import { useToast } from "shared/toast/ToastProvider";

function NewKeyBanner({
  name,
  apiKey,
  scopes,
  onDismiss,
}: {
  name: string;
  apiKey: string;
  scopes: string[];
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <div
      data-testid="sa-new-key"
      className="mb-4 rounded-lg border border-brand-brass/50 bg-brand-brass/10 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-brand-navy">
            {t("screens.serviceAccountsView.newKeyFor")} {name}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            {t("screens.serviceAccountsView.copyItNowItIsShownOnce")}
          </p>
          <code
            data-testid="sa-new-key-value"
            className="mt-2 block break-all rounded bg-white px-2 py-1 font-mono text-xs text-slate-900 ring-1 ring-slate-200"
          >
            {apiKey}
          </code>
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(apiKey);
              toast.push({ tone: "success", message: "Key copied" });
            }}
            className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
          >
            {t("screens.serviceAccountsView.copy")}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            {t("screens.serviceAccountsView.dismiss")}
          </button>
        </div>
      </div>
      <OutboundContract scopes={scopes} />
    </div>
  );
}

function CreateForm({
  onCreated,
}: {
  onCreated: (name: string, apiKey: string, scopes: string[]) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateServiceAccount();
  const toast = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);

  const toggle = (scope: string) =>
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );

  const submit = () => {
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), scopes },
      {
        onSuccess: (acct) => {
          onCreated(acct.name, acct.api_key, acct.scopes);
          setName("");
          setScopes([]);
        },
        onError: (err) =>
          toast.push({ tone: "error", message: "Create failed", detail: err.message }),
      },
    );
  };

  return (
    <section
      data-testid="sa-create"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h2 className="mb-2 text-base font-semibold text-slate-900">
        {t("screens.serviceAccountsView.newServiceAccount")}
      </h2>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            {t("screens.serviceAccountsView.name")}
          </span>
          <input
            dir="auto"
            data-testid="sa-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="grc-export"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <fieldset className="text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            {t("screens.serviceAccountsView.scopesReadOnly")}
          </span>
          <div className="flex flex-wrap gap-2">
            {SERVICE_SCOPES.map((scope) => (
              <label
                key={scope}
                className="flex items-center gap-1 text-xs text-slate-700"
              >
                <input
                  type="checkbox"
                  data-testid={`sa-scope-${scope}`}
                  checked={scopes.includes(scope)}
                  onChange={() => toggle(scope)}
                />
                <span className="font-mono">{scope}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <button
          type="button"
          data-testid="sa-create-button"
          onClick={submit}
          disabled={!name.trim() || create.isPending}
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {create.isPending
            ? t("screens.serviceAccountsView.creating")
            : t("screens.serviceAccountsView.createKey")}
        </button>
      </div>
    </section>
  );
}

function Row({
  account,
  onRotated,
}: {
  account: ServiceAccount;
  onRotated: (name: string, apiKey: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const rotate = useRotateServiceAccount();
  const update = useUpdateServiceAccount();
  const del = useDeleteServiceAccount();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <tr
      data-testid={`sa-row-${account.id}`}
      className="border-t border-slate-100 align-top"
    >
      <td className="py-2 pe-3 font-medium text-slate-900">{account.name}</td>
      <td className="py-2 pe-3 font-mono text-xs text-slate-500">
        {account.key_prefix}…
      </td>
      <td className="py-2 pe-3 text-xs text-slate-600">
        {account.scopes.length ? account.scopes.join(", ") : "—"}
      </td>
      <td className="py-2 pe-3">
        <button
          type="button"
          data-testid={`sa-toggle-${account.id}`}
          onClick={() =>
            update.mutate({ id: account.id, body: { enabled: !account.enabled } })
          }
          className={
            "rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 " +
            (account.enabled
              ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
              : "bg-amber-50 text-amber-700 ring-amber-200")
          }
        >
          {account.enabled
            ? t("screens.serviceAccountsView.enabled")
            : t("screens.serviceAccountsView.disabled")}
        </button>
      </td>
      <td className="py-2 pe-3 text-xs text-slate-500 tabular-nums">
        {/* Round 9 A1. This read "never" while a holder was pulling the
            estate on a schedule, because nothing wrote last_used_at.
            The count sits beside the instant deliberately: "last used
            an hour ago" and "last used an hour ago, 40,000 requests"
            are different facts about a third party. */}
        {account.last_used_at ? (
          <>
            {formatDateTime(account.last_used_at, i18n.language)}
            <span className="text-slate-400">
              {" · "}
              {t("screens.serviceAccountsView.requests", {
                count: account.request_count,
              })}
            </span>
          </>
        ) : (
          t("screens.serviceAccountsView.never")
        )}
      </td>
      <td className="py-2 text-xs">
        <button
          type="button"
          data-testid={`sa-rotate-${account.id}`}
          onClick={() =>
            rotate.mutate(account.id, {
              onSuccess: (a) => onRotated(a.name, a.api_key),
              onError: (err) =>
                toast.push({
                  tone: "error",
                  message: "Rotate failed",
                  detail: err.message,
                }),
            })
          }
          className="me-2 text-brand-brassDark hover:underline"
        >
          {t("screens.serviceAccountsView.rotate")}
        </button>
        {confirmDelete ? (
          <button
            type="button"
            data-testid={`sa-revoke-confirm-${account.id}`}
            onClick={() =>
              del.mutate(account.id, {
                onSuccess: () =>
                  toast.push({ tone: "warning", message: `Revoked ${account.name}` }),
              })
            }
            className="text-rose-700 hover:underline"
          >
            {t("screens.serviceAccountsView.confirmRevoke")}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`sa-revoke-${account.id}`}
            onClick={() => setConfirmDelete(true)}
            className="text-rose-700 hover:underline"
          >
            {t("screens.serviceAccountsView.revoke")}
          </button>
        )}
      </td>
    </tr>
  );
}

export function ServiceAccountsView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const accounts = useServiceAccounts({ enabled: isAdmin });
  const [newKey, setNewKey] = useState<{
    name: string;
    apiKey: string;
    scopes: string[];
  } | null>(null);

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Service accounts" />;
  }

  const rows = accounts.data ?? [];

  return (
    <article data-testid="service-accounts-view" className="max-w-5xl">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="verifier"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.serviceAccountsView.serviceAccounts")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          {t("screens.serviceAccountsView.apiKeysForB2bConsumersAGrc")}
        </p>
      </header>

      {newKey && (
        <NewKeyBanner
          name={newKey.name}
          apiKey={newKey.apiKey}
          scopes={newKey.scopes}
          onDismiss={() => setNewKey(null)}
        />
      )}

      <CreateForm
        onCreated={(name, apiKey, scopes) => setNewKey({ name, apiKey, scopes })}
      />

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        {rows.length === 0 ? (
          <p data-testid="sa-empty" className="text-sm text-slate-500">
            {t("screens.serviceAccountsView.noServiceAccountsYet")}
          </p>
        ) : (
          <table className="w-full text-start text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400">
                <th className="pb-1 pe-3 font-medium">
                  {t("screens.serviceAccountsView.name")}
                </th>
                <th className="pb-1 pe-3 font-medium">
                  {t("screens.serviceAccountsView.key")}
                </th>
                <th className="pb-1 pe-3 font-medium">
                  {t("screens.serviceAccountsView.scopes")}
                </th>
                <th className="pb-1 pe-3 font-medium">
                  {t("screens.serviceAccountsView.status")}
                </th>
                <th className="pb-1 pe-3 font-medium">
                  {t("screens.serviceAccountsView.lastUsed")}
                </th>
                <th className="pb-1 font-medium">
                  {t("screens.serviceAccountsView.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <Row
                  key={a.id}
                  account={a}
                  onRotated={(name, apiKey) =>
                    setNewKey({ name, apiKey, scopes: a.scopes })
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </article>
  );
}
